import { addDays } from 'date-fns'

import { ORDERS_PAGE as dict } from '@/resources/dict'
import { OrderStatus } from '@/resources/enums/orderStatus'
import { NOTE_KEYWORDS } from '@/pages/Order/constants'
import {
  addTimeZone,
  isToday,
  isTomorrow,
  millisecondsToMinutes,
  millisecondsToSeconds,
} from '../global'
import { DeliveryType } from '@/resources/enums/deliveryTypes'
import {
  MAX_ACCEPT_MINUTES,
  MAX_ACCEPT_MINUTES_TURBO,
  MAX_PICKUP_MINUTES,
  MAX_PICKUP_PENDING_MINUTES,
} from '@/pages/Orders/constants'
import { OrderColorHex } from '@/resources/enums/orders'
export * from './colors'

export const HOUR_IN_MS = 60 * 60 * 1000
export const HOURS_IN_DAY = 24
export const DAYS_IN_WEEK = 7

export const DEFAULT_HISTORY_LOOKBACK_HOURS = 2
export const CBE_HISTORY_LOOKBACK_HOURS = HOURS_IN_DAY * DAYS_IN_WEEK

const hoursBeforePeriod = hours =>
  new Date(Date.now() - hours * HOUR_IN_MS).toISOString()

export const isRatingLow = (orderRating = 0) => {
  const MINIMUM_REQUIRED_RATING = 5

  if (orderRating !== null && orderRating < MINIMUM_REQUIRED_RATING) {
    return true
  }

  return false
}

export const getRatingTime = (ratingTime = 0) => {
  /* time in seconds */
  const minute = 60
  const ratingTimeInMinutes = Number.parseInt(ratingTime / minute, 10)

  const hour = minute * 60
  const ratingTimeInHours = Number.parseInt(ratingTime / hour, 10)

  const day = hour * 24
  const ratingTimeInDays = Number.parseInt(ratingTime / day, 10)

  switch (true) {
    case ratingTimeInMinutes <= 0:
      return dict.ratingTime.lessThanOneMinute

    case ratingTime <= hour:
      return `${dict.ratingTime.ago} ${ratingTimeInMinutes} ${
        ratingTimeInMinutes <= 1
          ? dict.ratingTime.minute
          : dict.ratingTime.minutes
      }`

    case ratingTime > hour && ratingTime <= day:
      return `${dict.ratingTime.ago} ${ratingTimeInHours} ${
        ratingTimeInHours <= 1 ? dict.ratingTime.hour : dict.ratingTime.hours
      }`

    case ratingTime > day:
      return `${dict.ratingTime.ago} ${ratingTimeInDays} ${
        ratingTimeInDays <= 1 ? dict.ratingTime.day : dict.ratingTime.days
      }`

    default:
      return ''
  }
}

export const timeColors = {
  timeToSpare: '#1A7028',
  littleTime: '#826819',
  late: '#C3352B',
}

export const scheduledTimeColors = {
  GREEN: { background: '#EAF8EE', text: OrderColorHex.GREEN },
  YELLOW: { background: '#FFF7EB', text: OrderColorHex.YELLOW },
  RED: { background: '#FCECEB', text: OrderColorHex.RED },
}

export const SCHEDULED_ORDER_PRIORITIZED_TIME = 60 * 60 * 1000

export const DEFAULT_HISTORY_PAGE_VALUE = 1
export const DEFAULT_HISTORY_PER_PAGE_VALUE = 10

export const ordersHistoryQueryVariables = (
  perPage = DEFAULT_HISTORY_PER_PAGE_VALUE,
  lookbackHours = DEFAULT_HISTORY_LOOKBACK_HOURS
) => ({
  pagination: {
    page: DEFAULT_HISTORY_PAGE_VALUE,
    perPage,
  },
  period: {
    start: hoursBeforePeriod(lookbackHours),
    end: new Date().toISOString(),
  },
  migration: true,
})

export const isOrderChecked = (orderNumbers, orderNumber) =>
  orderNumbers.includes(orderNumber)

export const isOrderLate = (date, deliveryTime) => {
  const scheduledDate = addDays(addTimeZone(date), 1)
  scheduledDate.setHours(deliveryTime, '00', '00', '00')

  return scheduledDate < new Date()
}

export const formatProductName = name => {
  if (!name.length) return ''

  return name.split(/( - | \| )/)[0]
}

export const isRoomTemperature = (orderNote = '') =>
  orderNote?.includes(NOTE_KEYWORDS.ROOM_TEMPERATURE)

export const getDeliveryDay = (deliveryDay, deliveryDate) => {
  if (isToday(deliveryDate)) return dict.kanban.scheduled.delivery.today
  if (isTomorrow(deliveryDate)) return dict.kanban.scheduled.delivery.tomorrow
  return String(deliveryDay)
}

export const isOrderWithDeliveryType = (orderType, deliveryType) =>
  orderType === deliveryType

export const getInSeparationOrdersWithDeliveryType = (
  orders = [],
  deliveryType = DeliveryType.RECEIVE
) => {
  return orders.filter(
    ({ status, delivery }) =>
      (status === OrderStatus.POC_ACCEPTED ||
        status === OrderStatus.DM_ACCEPTED) &&
      isOrderWithDeliveryType(delivery.type, deliveryType)
  )
}

export const formatDelivery = (startDate, endDate, isAlert = false) => {
  if (!startDate || !endDate) return ''

  const start = new Date(startDate)
  const end = new Date(endDate)

  const endTime = end.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  if (isAlert) {
    const startFormatted = start.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    })
    const startTime = start.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })

    return `${dict.kanban.choppSchedule.to} ${startFormatted} ${dict.kanban.choppSchedule.tag[1]} ${startTime} ${dict.kanban.choppSchedule.tag[2]} ${endTime}`
  }

  const startDay = start.toLocaleDateString('pt-BR', { weekday: 'long' })
  const startFormatted = start.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const startTime = start.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return `${dict.kanban.choppSchedule.tag[0]} ${startDay} ${startFormatted} ${dict.kanban.choppSchedule.tag[1]} ${startTime} ${dict.kanban.choppSchedule.tag[2]} ${endTime}`
}

export const getOrderProgressBarPercent = (
  minutes,
  totalTime,
  isTurbo,
  isPickup,
  orderStatus
) => {
  let maxTime

  const isPickupPending =
    isPickup && !isTurbo && orderStatus === OrderStatus.PENDING

  const isPickupAccepted =
    isPickup && !isTurbo && orderStatus === OrderStatus.POC_ACCEPTED

  if (isPickupPending) {
    maxTime = MAX_PICKUP_PENDING_MINUTES
  } else if (isPickupAccepted) {
    maxTime = totalTime ?? MAX_PICKUP_MINUTES
  } else {
    maxTime = isTurbo
      ? (totalTime ?? MAX_ACCEPT_MINUTES_TURBO)
      : (totalTime ?? MAX_ACCEPT_MINUTES)
  }

  return 100 - (minutes * 100) / maxTime
}

export const hasOrderElapsedTimer = (deliveryType, hasChopp) =>
  (deliveryType === DeliveryType.RECEIVE ||
    deliveryType === DeliveryType.TURBO) &&
  !hasChopp

export const getScheduledOrderTotalTimeInMinutes = (
  isScheduled,
  deliveryDate
) => {
  if (!isScheduled || !deliveryDate) return undefined

  return millisecondsToMinutes(
    getTimestampFromDate(deliveryDate?.endDate) -
      getTimestampFromDate(deliveryDate?.startDate)
  )
}

export const getPickupOrderTotalTimeInMinutes = (
  pickupCreateDate,
  pickupEndDate
) => {
  return millisecondsToMinutes(pickupEndDate - pickupCreateDate)
}

export const getProgressBarTimeRemaining = (
  isScheduled,
  expiringTime,
  deliveryDate
) =>
  isScheduled
    ? millisecondsToSeconds(
        getTimestampFromDate(deliveryDate?.endDate) - new Date().getTime()
      )
    : expiringTime

export const getPickupProgressBarTimeRemaining = (
  isPickup,
  expiringTime,
  pickupEndDate
) =>
  isPickup ? millisecondsToSeconds(pickupEndDate - Date.now()) : expiringTime

export const getTimestampFromDate = date =>
  date ? addTimeZone(date)?.getTime() : 0
